import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { get, put, del } from "../services/apiClient";

/**
 * User Type
 * Represents a user in the system
 */
export interface User {
  id: string;
  firebaseUid: string;
  phone: string;
  email: string | null;
  role: "PROVIDER" | "CUSTOMER" | "ADMIN";
  fcmToken: string | null;
  createdAt: string;
  updatedAt: string;
  _count?: {
    businesses: number;
    bookings: number;
    reviews: number;
  };
}

/**
 * User List Response
 */
interface UserListResponse {
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * User List Params
 */
export interface UserListParams {
  page?: number;
  limit?: number;
  q?: string; // Search query (searches in phone and email)
  role?: "PROVIDER" | "CUSTOMER" | "ADMIN";
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Update User DTO
 */
export interface UpdateUserDto {
  email?: string;
  phone?: string;
  role?: "PROVIDER" | "CUSTOMER" | "ADMIN";
  fcmToken?: string;
}

/**
 * Query Keys
 * Centralized query keys for React Query cache management
 */
export const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (params: UserListParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, "detail"] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
};

/**
 * useUserList Hook
 * Fetches a paginated list of users
 *
 * @param params - Query parameters (page, limit, filters)
 * @param options - React Query options
 * @returns React Query result with user list data
 */
export const useUserList = (
  params: UserListParams = {},
  options?: Omit<UseQueryOptions<UserListResponse>, "queryKey" | "queryFn">,
) => {
  return useQuery<UserListResponse>({
    queryKey: userKeys.list(params),
    queryFn: () => get<UserListResponse>("/admin/users", params),
    ...options,
  });
};

/**
 * useUser Hook
 * Fetches a single user by ID
 *
 * @param id - User ID
 * @param options - React Query options
 * @returns React Query result with user data
 */
export const useUser = (
  id: string | undefined,
  options?: Omit<UseQueryOptions<User>, "queryKey" | "queryFn">,
) => {
  return useQuery<User>({
    queryKey: userKeys.detail(id || ""),
    queryFn: () => get<User>(`/users/${id}`),
    enabled: !!id,
    ...options,
  });
};

/**
 * useUpdateUser Hook
 * Updates an existing user
 *
 * @param options - React Query mutation options
 * @returns React Query mutation result
 */
export const useUpdateUser = (
  options?: UseMutationOptions<
    User,
    Error,
    { id: string; data: UpdateUserDto }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<User, Error, { id: string; data: UpdateUserDto }>({
    mutationFn: ({ id, data }) => put<User>(`/users/${id}`, data),
    onSuccess: (data, variables) => {
      // Invalidate user list and detail to refetch
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: userKeys.detail(variables.id),
      });
    },
    ...options,
  });
};

/**
 * useDeleteUser Hook
 * Deletes a user
 *
 * @param options - React Query mutation options
 * @returns React Query mutation result
 */
export const useDeleteUser = (
  options?: UseMutationOptions<void, Error, string>,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => del<void>(`/users/${id}`),
    onSuccess: () => {
      // Invalidate user list to refetch
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
    ...options,
  });
};
